import { useCallback, useRef } from 'react'

import { flushSync } from 'react-dom'

/**
 * Hook wrapping the View Transitions API for smooth DOM transitions.
 *
 * When supported, `startViewTransition(callback)` captures before/after snapshots
 * and GPU-composites the animation between them — no layout thrash.
 *
 * `isTransitioning` ref is true while the VT animation plays, allowing
 * expensive observers (e.g. ResizeObserver → ECharts resize) to skip
 * work that's invisible behind the VT screenshot overlay.
 *
 * Fallback: calls `callback()` directly (instant snap, no animation).
 */
export function useViewTransition() {
  const isTransitioning = useRef(false)

  const startViewTransition = useCallback((callback: () => void) => {
    if (
      typeof document !== 'undefined' &&
      'startViewTransition' in document &&
      typeof document.startViewTransition === 'function'
    ) {
      isTransitioning.current = true
      const vt = document.startViewTransition(() => {
        flushSync(callback)
      })
      vt.finished.then(
        () => {
          isTransitioning.current = false
        },
        () => {
          isTransitioning.current = false
        },
      )
    } else {
      callback()
    }
  }, [])

  return { startViewTransition, isTransitioning }
}
